import { useMemo, useState, useCallback, useLayoutEffect } from "react";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import {
  FormikTextField,
  FormikSelectField,
  FormikDateTimeField,
} from "src/components/FormikComponents/FormikWrappers";
import { Theme, Grid, Typography, Button } from "@mui/material";
import { IFlightAircraft, IFlightType, PolicyResource, INewGroundEvent } from "@vmosolutions/oc-interfaces";
import { EventTypes } from "../FlightBuilder";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState, IAirportOption } from "src/redux/reducers/StructuresReducer";
import { formatIdentFromAirport, updateAirportOptions } from "src/utils/airportHelper";
import { v1 } from "uuid";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { isEqual } from "lodash";
import { getBlockDurationString } from "./FlightRowEdit";
import { EventChip } from "src/components/Flights/EventChip";

interface IGroundEventRowEditProps {
  initialValues: INewGroundEvent;
  legCount: number;
  legIndex: number;
  mosaicPane: HTMLElement;
  onSubmit: (eventValues: INewGroundEvent) => void;
  theme: Theme;
  eventType?: EventTypes;
  displayAsUTC: boolean;
  setEditRow: React.Dispatch<React.SetStateAction<number>>;
  focusField?: string;
  addNewLeg: (p: boolean) => void;
}

export const defaultGroundEventValues: INewGroundEvent = {
  _id: undefined,
  scheduledStart: undefined,
  scheduledEnd: undefined,
  location: undefined,
  eventType: undefined,
  aircraft: undefined,
  note: undefined,
  comment: undefined,
};

export const getInitialValues = (defaultGroundEventType?: IFlightType): INewGroundEvent => {
  return { ...defaultGroundEventValues, _id: `temp-${v1()}`, eventType: defaultGroundEventType ?? undefined };
};

type NewGroundEventForm = FormikProps<INewGroundEvent>;

export const validateGroundEvent = (values: INewGroundEvent): any => {
  const { eventType, location, scheduledStart, scheduledEnd, aircraft, note } = values;
  // nested values have to be added here as empty objects.
  const errors: any = {};
  // Only validate if the form is dirty (has changes)
  if (!eventType) {
    errors.eventType = "Required";
  }
  if (!location) {
    errors.location = "Required";
  }
  if (!scheduledStart) {
    errors.scheduledStart = "Required";
  }
  if (!scheduledEnd) {
    errors.scheduledEnd = "Required";
  }
  if (!aircraft) {
    errors.aircraft = "Required";
  }

  // Validate small note size
  if (!!note && note.length > 100) {
    errors.note = "< 100 Letters";
  }

  // Check if scheduled arrival is after departure.
  if (!errors.scheduledStart && !errors.scheduledEnd && !!scheduledStart && !!scheduledEnd) {
    if (new Date(scheduledEnd) <= new Date(scheduledStart)) {
      errors.scheduledStart = "Event must start before it's end time.";
      errors.scheduledEnd = "Event must end after it's start time.";
    }
  }
  return errors;
};

/**
 * Show selected Flight Row in Edit Mode
 * @param props -
 */
export const GroundEventRowEdit = (props: IGroundEventRowEditProps): JSX.Element => {
  const {
    initialValues,
    legCount,
    legIndex,
    mosaicPane,
    onSubmit,
    theme,
    eventType,
    displayAsUTC,
    setEditRow,
    focusField,
    addNewLeg,
  } = props;
  const dispatch = useDispatch();
  const [showMoreRow, setShowMoreRow] = useState<boolean>(false);

  useLayoutEffect(() => {
    // On mount, auto focus the flight number field.
    setTimeout(() => {
      const field = focusField ?? "eventType";
      document.getElementById(field)?.focus();
    }, 50);
  }, [focusField]);

  // Retrieve Field Permission access check.
  const hasPermissions = useSelector((state: IRootState) => state.authentication.hasPermissions);

  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions, customerStructure } = structures;
  const { flightTypes } = customerStructure;

  const groundEventTypes = useMemo(() => {
    return flightTypes?.length ? flightTypes.filter((type: IFlightType) => !type.isFlight) : [];
  }, [flightTypes]);
  const aircraftOptions = customerStructure?.aircraft;

  /** PASS Flight values up to flight builder component */
  const handleSubmit = useCallback(
    (values: INewGroundEvent, helpers?: FormikHelpers<INewGroundEvent>): void => {
      onSubmit(values);
    },
    [onSubmit]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validate={validateGroundEvent}
    >
      {(eventForm: NewGroundEventForm) => {
        const { initialValues, values, isValid } = eventForm;

        // If values have changed and are valid, autosubmit form to update flight.
        if (!isEqual(initialValues, values)) {
          const valid = validateGroundEvent(values);
          if (Object.entries(valid)?.length === 0) {
            handleSubmit(values);
          }
        }

        /**
         * If user presses a key, add a new row if the following conditions are met.
         * row is valid, current last row is active, user has pressed (shift+enter) or (tab while on aircraft field)
         * @param event
         */
        const validateAndCreateNewRow = (event: React.KeyboardEvent): void => {
          const focusedField = document?.activeElement?.id;
          const lastRow = legIndex + 1 === legCount;
          if (
            isValid &&
            ((event?.code === "Enter" && event?.shiftKey) || (event?.code === "Tab" && focusedField === "aircraft"))
          ) {
            if (lastRow) addNewLeg(true);
            else addNewLeg(false);
          }
        };

        const blockDuration = getBlockDurationString(values?.scheduledStart, values?.scheduledEnd);

        return (
          <Form className={"form_styles"}>
            <Grid container direction="row" className="flight_row" onKeyDown={validateAndCreateNewRow}>
              <Grid container direction="row" flexWrap="nowrap" sx={{ width: "auto" }}>
                <Grid item className="checkbox">
                  <CheckboxField checked={true} onChange={() => setEditRow(-1)} />
                </Grid>
                <Grid item className="flight_leg">
                  <Typography variant="caption">{(legIndex + 1).toString()}</Typography>
                </Grid>
                <Grid
                  container
                  direction="row"
                  className={`required_fields ${isValid ? "" : "invalid"}`}
                  flexWrap="nowrap"
                >
                  <Grid item style={{ width: 130 }}>
                    <EventChip data={values} />
                  </Grid>
                  <FormikSelectField
                    className={"event_type"}
                    name="eventType"
                    label=""
                    isMulti={false}
                    options={groundEventTypes.sort((a, b) => {
                      return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                    })}
                    getOptionLabel={(type: IFlightType) => `${type.title} - ${type.description}`}
                    getOptionValue={(eventType: IFlightType) => eventType._id}
                    placeholder=""
                    isSearchable={true}
                    menuPortalTarget={mosaicPane}
                    menuPosition={"fixed"}
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "eventType")}
                  />

                  <FormikSelectField
                    className={"location"}
                    label=""
                    name="location"
                    format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                    parse={(airport: IAirportOption) => airport?.ident}
                    onInputChange={(ident: string) => {
                      updateAirportOptions(ident, airportOptions, dispatch);
                    }}
                    options={Object.values(airportOptions)}
                    // For search purposes, include ICAO, IATA, and AIRPORT NAME.
                    // IATA is omitted when absent so the literal "null" never enters the search text.
                    getOptionLabel={(airport: IAirportOption) =>
                      `${airport.ident} - ${airport.iata ? `${airport.iata} - ` : ""}${airport.name}`
                    }
                    // Display ICAO only.
                    formatOptionLabel={(airport: IAirportOption) => `${airport.ident}`}
                    getOptionValue={(airport: IAirportOption) => airport.ident}
                    isSearchable={true}
                    menuPortalTarget={mosaicPane}
                    noOptionsMessage={() => "Search for airport name or code"}
                    isMulti={false}
                    menuPosition={"fixed"}
                    openMenuOnFocus={false}
                    isClearable={false}
                    placeholder=""
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "location")}
                  />

                  <FormikDateTimeField
                    className="scheduled_start"
                    label=""
                    name="scheduledStart"
                    showAirportTime={values?.location}
                    localizeDisplay={!displayAsUTC}
                    hideLocalizeBtn={true}
                    isEpoch={true}
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "scheduledStart")}
                  />

                  <FormikDateTimeField
                    className="scheduled_end"
                    label=""
                    name="scheduledEnd"
                    showAirportTime={values?.location}
                    localizeDisplay={!displayAsUTC}
                    hideLocalizeBtn={true}
                    isEpoch={true}
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "scheduledEnd")}
                  />
                  <Typography className="block_duration" variant="caption">
                    {blockDuration}
                  </Typography>
                  <FormikSelectField
                    className="aircraft"
                    name="aircraft"
                    label=""
                    isMulti={false}
                    options={aircraftOptions}
                    // For search purposes, include registration, aircraftType, aircraftType title.
                    getOptionLabel={(aircraft: IFlightAircraft) =>
                      `${aircraft.registration} (${aircraft.aircraftType && aircraft.aircraftType.title})`
                    }
                    // Display registration only.
                    formatOptionLabel={(aircraft: IFlightAircraft) => `${aircraft.registration}`}
                    getOptionValue={(aircraft: IFlightAircraft) => aircraft?._id}
                    placeholder=""
                    menuPortalTarget={mosaicPane}
                    menuPosition={"fixed"}
                    isSearchable={true}
                    isClearable={true}
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "aircraft")}
                  />
                </Grid>

                <Grid container direction="row" className="optional_fields" flexWrap="nowrap">
                  <Button
                    onClick={() => setShowMoreRow((prev) => (prev ? false : true))}
                    color="info"
                    autoFocus={true}
                    sx={{ fontSize: "11px", padding: "2px 8px", margin: 0 }}
                  >
                    {showMoreRow ? "less fields..." : "more fields..."}
                  </Button>
                </Grid>
              </Grid>

              {showMoreRow && (
                <Grid container direction="row" className="more_optional_fields">
                  <FormikTextField
                    className="formik_field textfield multiline_long note"
                    label="Small Note"
                    name="note"
                    multiline={true}
                    minRows={2}
                    maxRows={5}
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "note")}
                  />

                  <FormikTextField
                    className="formik_field textfield multiline_long comment"
                    label="Comment"
                    name="comment"
                    multiline={true}
                    minRows={2}
                    maxRows={5}
                    disabled={!hasPermissions(PolicyResource.GroundEvent, "comment")}
                  />
                </Grid>
              )}
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
